import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Text } from '~/components/core';
import { StrategyStatus } from '~/api-services/AIStrategyBuilderService/types';
import { statusColor } from '~/constants/strategyConstants';
import { useStyles } from '~/hooks';
import { useTheme } from '~/hooks/useTheme';
import { FONT_SIZE_S } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    status: Extract<StrategyStatus, 'enabled' | 'disabled' | 'discarded'>;
    // Toggle for enabled/disabled; Clone & edit for discarded.
    actionLabel: string;
    onAction?: () => void;
};

const BANNER_COPY: Record<Props['status'], string> = {
    enabled: 'ai-strategy-banner-live',
    disabled: 'ai-strategy-banner-inactive',
    discarded: 'ai-strategy-banner-discarded',
};

// Slim status strip above the tabs for settled strategies, with the one relevant action.
export default function StatusBanner({ status, actionLabel, onAction }: Props) {
    const styles = useStyles(stylesGenerator);
    const theme = useTheme();
    return (
        <View style={[styles.container, { borderLeftColor: statusColor(theme, status) }]}>
            <Text style={styles.text}>{BANNER_COPY[status]}</Text>
            {onAction ? (
                <Pressable onPress={onAction} style={({ pressed }) => [styles.action, pressed && styles.pressed]}>
                    <Text fontType="LATO_BOLD" style={styles.actionText}>
                        {actionLabel}
                    </Text>
                </Pressable>
            ) : null}
        </View>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: theme.backgroundSecondary,
            borderLeftWidth: scaleSize(3),
            paddingVertical: scaleSize(9),
            paddingHorizontal: scaleSize(12),
            marginHorizontal: scaleSize(12),
            marginTop: scaleSize(12),
            marginBottom: scaleSize(8),
            borderRadius: scaleSize(6),
        },
        text: {
            flex: 1,
            fontSize: FONT_SIZE_S,
            color: theme.text,
            marginRight: scaleSize(8),
        },
        action: {
            paddingVertical: scaleSize(4),
            paddingHorizontal: scaleSize(10),
        },
        pressed: {
            opacity: 0.6,
        },
        actionText: {
            fontSize: FONT_SIZE_S,
            color: theme.button,
        },
    });
